import type {
  LinkOffer,
  NoyauAnalystPrompts,
  NoyauTrial,
  NoyauTrialList,
  NoyauTrialStartSource,
  RangementTrial,
  RangementTrialList,
  RangerPrompts,
  SessionAdvance,
  SessionDetail,
  SessionList,
  SessionMessage,
  SessionView,
  TurnPrompts,
} from '@matter_extractor/shared';
import {
  linkOfferSchema,
  noyauAnalystPromptsSchema,
  noyauTrialListSchema,
  noyauTrialSchema,
  rangementTrialListSchema,
  rangementTrialSchema,
  rangerPromptsSchema,
  sessionDetailSchema,
  sessionErrorSchema,
  sessionListSchema,
  sessionStartedSchema,
  sessionViewSchema,
  turnPromptsSchema,
} from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The calls of the session (§5.1 to §5.3, §9.5, §12.1) - the person's door, the
 * session itself, and the operator's list.
 *
 * ## Why a file of its own rather than a family in `lib/api.ts`
 *
 * The argument `lib/links-api.ts` makes, at the same moment of the same cycle:
 * `api.ts` is edited by two other lots in parallel, and a merge inside a
 * four-hundred-line module goes wrong in the one place nobody looks - a request
 * that silently keeps the other branch's shape. `ApiError` is IMPORTED from
 * there and never re-declared, or `instanceof` would answer false on one of the
 * two classes.
 *
 * ## Two credentials, and neither of them is the operator's
 *
 * The public calls carry NO cookie and no token: the secret and the session
 * token are in the PATH, and they are the whole credential (§5.1, §5.2). Only
 * `/api/sessions` goes through the administration door, so this file keeps two
 * inits rather than one - a public call made with `credentials: 'include'`
 * would send the operator's cookie to a person's browser route for no reason.
 *
 * Every answer is PARSED before it is returned: a response is an external input
 * even when we wrote the server.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

const JSON_HEADERS = { 'content-type': 'application/json' } as const;

const ADMIN_INIT = {
  headers: JSON_HEADERS,
  credentials: 'include',
} as const satisfies RequestInit;

/**
 * The one behaviour this helper must keep: `details` falls back to the WHOLE
 * body, because a business refusal answers the contract body with no `details`
 * key at all - and the screens word a refusal from its `code`.
 */
async function send(path: string, init: RequestInit): Promise<Response> {
  const response = await fetch(`${API_URL}${path}`, init);
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response;
}

async function request(path: string, init: RequestInit): Promise<unknown> {
  const response = await send(path, init);
  // 204 and 202 are the two answers of this file with NO body, and calling
  // `.json()` on either throws a `SyntaxError` that would reach a screen as
  // « la requête a échoué » on a request the server accepted. 202 is the
  // message of §9.5: « répond 202 et un corps vide », everything it produces
  // arriving on the channel instead.
  if (response.status === 204 || response.status === 202) return null;
  return response.json();
}

const publicCall = (path: string, init: RequestInit = {}): Promise<unknown> =>
  request(path, { headers: JSON_HEADERS, ...init });

/* ------------------------------------------------------------- the door */

/**
 * `GET /api/l/{secret}` (issue #59) - the same answer for an unknown secret, a
 * revoked link, an expired one and a link whose entries stopped resolving.
 *
 * Read and never redefined by this lot: the choice page draws what it offers,
 * and the start below is the gesture that follows it.
 */
export function fetchLinkOffer(secret: string): Promise<LinkOffer> {
  return publicCall(`/api/l/${encodeURIComponent(secret)}`).then((body) =>
    linkOfferSchema.parse(body),
  );
}

/**
 * `POST /api/l/{secret}/sessions` - « Commencer », « Recommencer », and the
 * direct start of a single-entry link.
 *
 * Hands back the TOKEN and nothing else: the browser navigates to
 * `/s/<jeton>` and reads the session there.
 */
export function startSession(secret: string, experienceId: string | null): Promise<string> {
  return publicCall(`/api/l/${encodeURIComponent(secret)}/sessions`, {
    method: 'POST',
    body: JSON.stringify({ experienceId }),
  }).then((body) => sessionStartedSchema.parse(body).token);
}

/* ---------------------------------------------------------- the session */

/** `GET /api/s/{token}` - the status, the page, the values, the frozen content. */
export function fetchSession(token: string): Promise<SessionView> {
  return publicCall(`/api/s/${encodeURIComponent(token)}`).then((body) =>
    sessionViewSchema.parse(body),
  );
}

/** `POST /api/s/{token}/advance` - the values of the page, then the next one. */
export function advanceSession(token: string, body: SessionAdvance): Promise<SessionView> {
  return publicCall(`/api/s/${encodeURIComponent(token)}/advance`, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then((answer) => sessionViewSchema.parse(answer));
}

/**
 * `POST /api/s/{token}/message` - what the person just wrote (§9.5).
 *
 * It answers **202 and an empty body**, and there is nothing to parse: « le
 * tour n'a pas encore eu lieu, et tout ce qu'il produit arrive sur le canal ».
 * So this returns `void` rather than a view, and the screen learns what
 * happened from `typing`, the `delta`s and `message` - never from this call.
 *
 * What it CAN answer is a refusal, and those matter: 409 `turn-in-flight` while
 * a turn is being played, `session-over` past the 24 hours or on a protocol
 * that closed itself, `page-mismatch` off the protocol page. Each arrives as an
 * `ApiError` whose `details.code` the screen words itself, at the régime of
 * every other refusal in this repo.
 */
export function sendMessage(token: string, text: string): Promise<void> {
  return publicCall(`/api/s/${encodeURIComponent(token)}/message`, {
    method: 'POST',
    body: JSON.stringify({ text } satisfies SessionMessage),
  }).then(() => undefined);
}

/** The address of the SSE channel (§9.5). Built here so one file spells the API base. */
export function sessionEventsUrl(token: string): string {
  return `${API_URL}/api/s/${encodeURIComponent(token)}/events`;
}

/* --------------------------------------------------------- the operator */

/**
 * The ten filters of §12.1 (relecture.md §13, rejeu.md §12) plus the trash, as the screen holds them.
 *
 * `SessionFilters` of `shared` is what the SERVER validates, with `trash`
 * defaulted and every other member optional. This is the same set seen from the
 * address bar, where every member can legitimately be « not set » - hence
 * `| undefined` spelled out on each: under `exactOptionalPropertyTypes` an
 * optional property refuses an explicit `undefined`, and « clear this filter »
 * IS an explicit undefined.
 */
export type SessionListFilters = {
  readonly experienceId?: string | undefined;
  readonly status?: string | undefined;
  readonly linkId?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly protocolId?: string | undefined;
  readonly personaId?: string | undefined;
  readonly criterionId?: string | undefined;
  /** The RETAINED noyau, by its triplet (noyau.md §9.2). */
  readonly noyau?: string | undefined;
  /** The ninth: the sessions whose review holds something, or nothing (relecture.md §13). */
  readonly review?: 'with' | 'without' | undefined;
  /** The tenth: the sessions Claude played in the place of a person, or those a person played (rejeu.md §12). */
  readonly replay?: 'with' | 'without' | undefined;
  /** The origin (vitrine.md section 8.2): the sessions brought up from the prod, or those born here. */
  readonly origin?: 'prod' | 'dev' | undefined;
  readonly trash?: boolean | undefined;
};

/**
 * The query string of the list, built in ONE place.
 *
 * The keys are the server's (`experience`, `protocol`, …) and not the screen's
 * (`experienceId`, `protocolId`): the route declares the short ones, and a
 * second spelling here would be a filter that silently never applies. `trash`
 * travels only when true - a `trash=0` on every read would put a parameter in
 * every pasted link for the state that is already the default.
 */
function listQuery(filters: SessionListFilters): string {
  const query = new URLSearchParams();
  if (filters.experienceId) query.set('experience', filters.experienceId);
  if (filters.status) query.set('status', filters.status);
  if (filters.linkId) query.set('link', filters.linkId);
  if (filters.from) query.set('from', filters.from);
  if (filters.to) query.set('to', filters.to);
  if (filters.protocolId) query.set('protocol', filters.protocolId);
  if (filters.personaId) query.set('persona', filters.personaId);
  if (filters.criterionId) query.set('criterion', filters.criterionId);
  if (filters.noyau) query.set('noyau', filters.noyau);
  if (filters.review) query.set('review', filters.review);
  if (filters.replay) query.set('replay', filters.replay);
  if (filters.origin) query.set('origin', filters.origin);
  if (filters.trash === true) query.set('trash', '1');
  return query.size === 0 ? '' : `?${query.toString()}`;
}

/** `GET /api/sessions` - the list of §12.1, behind the administration door. */
export function fetchSessions(filters: SessionListFilters = {}): Promise<SessionList> {
  return request(`/api/sessions${listQuery(filters)}`, ADMIN_INIT).then((body) =>
    sessionListSchema.parse(body),
  );
}

/**
 * The cache key BOTH reader screens hold a session under.
 *
 * Written once, here, beside the call it keys. The parcours and the
 * conversation are « deux vues d’une lecture » (§12.2), so they must share it:
 * two keys over one document means a conversation opened from a parcours
 * re-fetches what the parcours already holds, and a session trashed on one screen
 * stays alive in the other one s copy. The two spellings existed for a day,
 * each written in its own worktree, and this is what closed them.
 *
 * Scoped by the signed-in operator like every other administration read: a
 * second person signing in on the same browser must not inherit the first
 * one’s cache.
 */
export const SESSION_DETAIL_KEY = (email: string | null, id: string): readonly unknown[] => [
  'session-detail',
  email,
  id,
];

/**
 * `GET /api/sessions/{id}` - ONE call for the parcours of §12.2 AND the
 * conversation of §12.3.
 *
 * The two screens are two views of one reading, so they share a query key: a
 * conversation opened from a parcours must not re-fetch what the parcours
 * already holds. What this never carries is the prompts served to the seats -
 * they have their own route below, read only when somebody opens the Brut
 * layer of a turn.
 */
export function fetchSessionDetail(id: string): Promise<SessionDetail> {
  return request(`/api/sessions/${encodeURIComponent(id)}`, ADMIN_INIT).then((body) =>
    sessionDetailSchema.parse(body),
  );
}

/**
 * `GET /api/sessions/{id}/turns/{turnId}/prompts` - what each seat was served
 * on one turn (§8.7).
 *
 * Read on a CLICK and never with the session: it is the biggest thing a turn
 * produces and the least often looked at, which is the whole reason it sits
 * behind its own route. The screen mounts it lazily, the régime the protocol
 * editor already applies to a criterion's card.
 */
export function fetchTurnPrompts(sessionId: string, turnId: string): Promise<TurnPrompts> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}/prompts`;
  return request(path, ADMIN_INIT).then((body) => turnPromptsSchema.parse(body));
}

/**
 * `GET /api/sessions/{id}/bubbles/{bubbleId}/prompts` - what the interviewer was
 * served for the bubble of a page played in the conversation (prolongation.md §7,
 * issue #186): the same shape as a turn's, the bubble's identifier in `turnId`,
 * and a read on a CLICK for the same reason.
 */
export function fetchBubblePrompts(sessionId: string, bubbleId: string): Promise<TurnPrompts> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/bubbles/${encodeURIComponent(bubbleId)}/prompts`;
  return request(path, ADMIN_INIT).then((body) => turnPromptsSchema.parse(body));
}

/**
 * `GET /api/sessions/{id}/noyau/prompts` - what the analyst of the noyau was
 * served, call by call (noyau.md §8, issue #152). Read on a CLICK, at the
 * régime of the prompts of a turn: the matter of the whole session is in it.
 */
export function fetchNoyauPrompts(sessionId: string): Promise<NoyauAnalystPrompts> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/noyau/prompts`;
  return request(path, ADMIN_INIT).then((body) => noyauAnalystPromptsSchema.parse(body));
}

/**
 * `POST /api/sessions/{id}/noyau/trials` - a TRIAL of the analyst (noyau.md
 * §9.4, issue #164), answered at once with the trial `pending`, the analyst
 * running behind it. A refusal is a 409 whose body is the trial's own contract,
 * read on `ApiError.details` (`noyauTrialRefusalOf`).
 *
 * The body is optional, and a bare press reads the matter of the session as it
 * always did: only another `source` (relecture.md §11) is written into one.
 */
export function startNoyauTrial(
  sessionId: string,
  source: NoyauTrialStartSource = { kind: 'session' },
): Promise<NoyauTrial> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/noyau/trials`;
  const body = source.kind === 'session' ? {} : { body: JSON.stringify({ source }) };
  return request(path, { ...ADMIN_INIT, method: 'POST', ...body }).then((answer) =>
    noyauTrialSchema.parse(answer),
  );
}

/**
 * `GET /api/sessions/{id}/noyau/trials/{trialId}` - one trial as it stands, its
 * served input included; 404 for a running trial a restart of the server lost.
 */
export function fetchNoyauTrial(sessionId: string, trialId: string): Promise<NoyauTrial> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/noyau/trials/${encodeURIComponent(trialId)}`;
  return request(path, ADMIN_INIT).then((body) => noyauTrialSchema.parse(body));
}

/** The key of one trial, apart from the detail's: a trial is not the session, and never changes it. */
export const NOYAU_TRIAL_KEY = (sessionId: string, trialId: string) =>
  ['session-noyau-trial', sessionId, trialId] as const;

/**
 * `GET /api/sessions/{id}/noyau/trials` - the trials of the session, newest
 * first (issue #167): the one running, then those kept, without what the
 * analyst was served, which the read of one trial carries.
 */
export function fetchNoyauTrials(sessionId: string): Promise<NoyauTrialList> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/noyau/trials`;
  return request(path, ADMIN_INIT).then((body) => noyauTrialListSchema.parse(body));
}

/** The key of the list, which a settled trial and a new start invalidate. */
export const NOYAU_TRIALS_KEY = (sessionId: string) => ['session-noyau-trials', sessionId] as const;

/**
 * `GET /api/sessions/{id}/rangement/prompts` - what the ranger of the session
 * was served, call by call (scenario-succes.md §7.1, §9, issue #177). Read on a
 * CLICK, at the régime of the analyst's: the matter of the whole session is in it.
 */
export function fetchRangerPrompts(sessionId: string): Promise<RangerPrompts> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/rangement/prompts`;
  return request(path, ADMIN_INIT).then((body) => rangerPromptsSchema.parse(body));
}

/**
 * `POST /api/sessions/{id}/rangement/trials` - a TRIAL of the ranger
 * (scenario-succes.md §7.3), answered at once with the trial `pending`, the
 * ranger running behind it. A refusal is a 409 whose body is the trial's own
 * contract, read on `ApiError.details` (`rangementTrialRefusalOf`).
 */
export function startRangementTrial(sessionId: string): Promise<RangementTrial> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/rangement/trials`;
  return request(path, { ...ADMIN_INIT, method: 'POST' }).then((body) =>
    rangementTrialSchema.parse(body),
  );
}

/**
 * `GET /api/sessions/{id}/rangement/trials/{trialId}` - one trial as it stands,
 * its served input included; 404 for a running trial a restart of the server lost.
 */
export function fetchRangementTrial(sessionId: string, trialId: string): Promise<RangementTrial> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/rangement/trials/${encodeURIComponent(trialId)}`;
  return request(path, ADMIN_INIT).then((body) => rangementTrialSchema.parse(body));
}

/** The key of one trial of the ranger, apart from the detail's: a trial is not the session. */
export const RANGEMENT_TRIAL_KEY = (sessionId: string, trialId: string) =>
  ['session-rangement-trial', sessionId, trialId] as const;

/**
 * `GET /api/sessions/{id}/rangement/trials` - the trials of the session, newest
 * first: the one running, then those kept, without what the ranger was served.
 */
export function fetchRangementTrials(sessionId: string): Promise<RangementTrialList> {
  const path = `/api/sessions/${encodeURIComponent(sessionId)}/rangement/trials`;
  return request(path, ADMIN_INIT).then((body) => rangementTrialListSchema.parse(body));
}

/** The key of the list, which a settled trial and a new start invalidate. */
export const RANGEMENT_TRIALS_KEY = (sessionId: string) =>
  ['session-rangement-trials', sessionId] as const;

/** `DELETE /api/sessions/{id}` - the trash of §12.4. Idempotent, answers 204. */
export function trashSession(id: string): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}`, {
    ...ADMIN_INIT,
    method: 'DELETE',
  }).then(() => undefined);
}

/**
 * `POST /api/sessions/{id}/interruption` - the operator's gesture « cette session est
 * finie » (issue #199, interruption.md §9). No body: the author is read by the server,
 * never sent. It answers the row of the session, which nothing reads: the screens read the
 * detail and the list again, the stamps being the server's, and a body parsed here could
 * only turn a gesture that DID happen into an error.
 * A refusal is a 409 whose `code` the box words (`lib/session-interruption.ts`).
 */
export function interruptSession(id: string): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}/interruption`, {
    ...ADMIN_INIT,
    method: 'POST',
  }).then(() => undefined);
}

/** `POST /api/sessions/{id}/restore` - out of the trash (§12.4). Idempotent. */
export function restoreSession(id: string): Promise<void> {
  return request(`/api/sessions/${encodeURIComponent(id)}/restore`, {
    ...ADMIN_INIT,
    method: 'POST',
  }).then(() => undefined);
}

/** The three files the server renders (§12.5, §12.6). « PDF » is the print of `html`. */
export type SessionExportFormat = 'html' | 'md' | 'json';

/**
 * The three downloads of §12.5 and §12.6, as ADDRESSES rather than as fetches.
 *
 * A download is the browser's job: an anchor with `download` hands the file to
 * the operating system with the server's own filename and content type, where a
 * `fetch` would pull the whole document into memory to hand it straight back.
 * The cookie rides along because these are same-site navigations to the guarded
 * API, exactly like every other `credentials: 'include'` call of this file.
 */
export function sessionExportUrl(id: string, format: SessionExportFormat): string {
  return `${API_URL}/api/sessions/${encodeURIComponent(id)}/export.${format}`;
}

/**
 * The name the server gives the file - `session-<id>.html` and its two siblings.
 *
 * Written on the anchor's `download` as well, for a same-origin deployment
 * where the browser honours it; the server's `Content-Disposition` names the
 * file everywhere else, so the two must never disagree.
 */
export function sessionExportFileName(id: string, format: SessionExportFormat): string {
  return `session-${id}.${format}`;
}

/**
 * `GET /api/sessions/{id}/export.html` READ as text - the fourth export, « PDF »
 * (§12.6, H107).
 *
 * The one export that is fetched rather than downloaded: the screen loads the
 * document in an isolated frame and prints it from there, so it needs the
 * text, not a file. A refusal - 409 `session-in-trash` on a session trashed in
 * another tab meanwhile - arrives as an `ApiError` like every other one of
 * this file (`isSessionInTrash`).
 */
export function fetchSessionExportHtml(id: string): Promise<string> {
  return send(`/api/sessions/${encodeURIComponent(id)}/export.html`, {
    credentials: 'include',
  }).then((response) => response.text());
}

/**
 * The refusal the four exports answer for a session in the trash (§12.5, H116).
 *
 * Read off the SERVER's contract, `sessionErrorSchema` of `shared`, whose `code`
 * is `sessionRefusalCodeSchema`: a body that does not parse is not this refusal,
 * and a renamed code breaks the typecheck here rather than the sentence.
 */
export function isSessionInTrash(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  const refusal = sessionErrorSchema.safeParse(error.details);
  return refusal.success && refusal.data.code === 'session-in-trash';
}
